/**
 * B000202 商品分類照会
 */
import { Column, Query } from "@material-table/core";
import { B000202Find, B000202FindRequest, catchRestError } from "clients/rest";
import {
  SText,
  SCodeSelect,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  STextField,
  msgState,
} from "components";
import { Shohinbun, SCode, getSCode } from "messages/codes";
import { labels } from "messages/labels";
import { createRef, useContext, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { mapIds } from "services";
import { focusState, SDialogContext, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = { shohinbun: SCode } & Partial<{
  shodaibunCdFrom: string;
  shodaibunCdTo: string;
  shochubunCdFrom: string;
  shochubunCdTo: string;
  shoshobunCdFrom: string;
  shoshobunCdTo: string;
  name: string;
}>;

// テーブル定義
type Data = Partial<{
  gyoNo: number;
  shodaibunCd: string;
  shodaibunNm: string;
  shochubunCd: string;
  shochubunNm: string;
  shoshobunCd: string;
  shoshobunNm: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  shohinbun: getSCode(Shohinbun, "0")!,
  shodaibunCdFrom: "",
  shodaibunCdTo: "",
  shochubunCdFrom: "",
  shochubunCdTo: "",
  shoshobunCdFrom: "",
  shoshobunCdTo: "",
  name: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行　", width: "2rem", align: "right" },
  { field: "shodaibun", title: "大分類コード", width: "7rem" },
  { field: "shohindaibunruiNm", title: "名称", width: "15rem" },
  { field: "shochubun", title: "中分類コード", width: "7rem" },
  { field: "shohinchubunruiNm", title: "名称", width: "15rem" },
  { field: "shoshobun", title: "小分類コード", width: "7rem" },
  { field: "shohinshobunruiNm", title: "名称", width: "15rem" },
];
// フォーカスIDの設定
const focusId = "B000202";

//B000202商品分類照会
export const B000202 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};
const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするRef
  const isDefaultPage = useRef(false);
  // ダイアログで処理を行う関数
  const handleSubmit = useContext(SDialogContext);
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });

  // フォーカス関数
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);

  // 画面遷移
  const navigate = useNavigate();

  //初期表示
  useEffect(() => {
    focus(1);
  });

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (data: Data) => {
    handleSubmit(data);
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }
      // 検索フォームのデータを取得する。
      const fields = methods.getValues();
      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;
      // 検索パラメータを作成する。
      const param: B000202FindRequest = {
        shohinbun: fields.shohinbun!.value,
        shodaibunCdFrom: fields.shodaibunCdFrom,
        shodaibunCdTo: fields.shodaibunCdTo,
        shochubunCdFrom: fields.shochubunCdFrom,
        shochubunCdTo: fields.shochubunCdTo,
        shoshobunCdFrom: fields.shoshobunCdFrom,
        shoshobunCdTo: fields.shoshobunCdTo,
        name: fields.name,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        sort: ["shodaibun,asc"],
      };
      // API呼び出し
      B000202Find(param)
        // 正常取得時
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        // エラー時
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="商品分類照会"
        gridTemplateColumns="8rem 3rem 1rem 3rem 2rem 8rem 3rem 1rem 3rem 2rem 8rem 3rem 1rem 3rem "
      >
        <SLabel>商品分類</SLabel>
        <SCodeSelect
          {...register(1)}
          options={Shohinbun}
          name="shohinbun"
          gridColumn="span 3"
        />
        <SLabel gridRow="2">商品大分類コード</SLabel>
        <SNumberField
          name="shodaibunCdFrom"
          code
          size={3}
          {...register(2)}
          gridRow="2"
        />
        <SText gridRow="2">～</SText>
        <SNumberField
          name="shodaibunCdTo"
          code
          size={3}
          {...register(3)}
          gridRow="2"
        />
        <SLabel gridRow="2" gridColumn="6">
          商品中分類コード
        </SLabel>
        <SNumberField
          name="shochubunCdFrom"
          code
          size={3}
          {...register(4)}
          gridRow="2"
          gridColumn="7"
        />
        <SText gridRow="2" gridColumn="8">
          ～
        </SText>
        <SNumberField
          name="shodchubunCdTo"
          code
          size={3}
          {...register(5)}
          gridRow="2"
          gridColumn="9"
        />
        <SLabel gridRow="2" gridColumn="11">
          商品小分類コード
        </SLabel>
        <SNumberField
          name="shoshobunCdFrom"
          code
          size={3}
          {...register(6)}
          gridRow="2"
          gridColumn="12"
        />
        <SText gridRow="2" gridColumn="13">
          ～
        </SText>
        <SNumberField
          name="shoshobunCdTo"
          code
          size={3}
          {...register(7)}
          gridRow="2"
          gridColumn="14"
        />
        <SLabel gridColumn="1">名称</SLabel>
        <STextField
          name="name"
          size={20}
          gridColumn="span 3"
          {...register(8)}
        />
        <SNormalButton
          label={labels.buttons.search}
          gridRow="4"
          gridColumn="13"
          {...register(4)}
          onClick={handleClickSearchButton}
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyoNo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          handleDoubleClick={handleDoubleClickTableRow}
        />
      </SFrame>
    </FormProvider>
  );
};
